import {
  CancelFilledOutlined,
  CheckCircleFilledOutlined,
  DoNotDisturbOnFilledOutlined,
  PendingOutlined,
  WarningOutlined,
} from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import type { FileRoutesByFullPath } from "routeTree.gen"

import type { SoftReceivedLotFormValues } from "./schemas"
import type { GoodsReceiptColumnKey } from "./types"

import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { As400SyncStatus, GoodsReceiptStatus, ProductTestStatus } from "@/graphql/codegen/graphql"
import { PURCHASE_ORDER_DOCUMENT_TYPE_OPTIONS } from "@/screens/Procurement/PurchaseOrder/constants"
import colors from "@/styles/colors"

/**
 * Configuration for which columns to display for each status
 */
export const GOODS_RECEIPT_DESKTOP_COLUMNS_CONFIG: Record<
  Exclude<GoodsReceiptStatus, GoodsReceiptStatus.PreCheckedIn>,
  GoodsReceiptColumnKey[]
> = {
  [GoodsReceiptStatus.Incoming]: [
    "agrNumber",
    "expectedDate",
    "trackingNumbers",
    "supplierName",
    "warehouse",
    "actions",
  ],
  [GoodsReceiptStatus.SoftReceived]: [
    "grNumber",
    "softReceivedOn",
    "trackingNumbers",
    "holdLocation",
    "supplierName",
    "warehouse",
    "actions",
  ],
  [GoodsReceiptStatus.CheckedIn]: [
    "grNumber",
    "checkedInOn",
    "trackingNumbers",
    "holdLocation",
    "supplierName",
    "warehouse",
    "actions",
  ],
  [GoodsReceiptStatus.Stored]: ["grNumber", "expectedDate", "trackingNumbers", "supplierName", "warehouse", "actions"],
} as const

export const GOODS_RECEIPT_TABLET_COLUMNS_CONFIG: Record<
  Exclude<GoodsReceiptStatus, GoodsReceiptStatus.PreCheckedIn>,
  GoodsReceiptColumnKey[]
> = {
  [GoodsReceiptStatus.Incoming]: ["agrNumber", "expectedDate", "trackingNumbers", "supplierName"],
  [GoodsReceiptStatus.SoftReceived]: ["grNumber", "trackingNumbers", "holdLocation", "supplierName", "softReceivedOn"],
  [GoodsReceiptStatus.CheckedIn]: ["grNumber", "holdLocation", "checkedInOn", "actions"],
  [GoodsReceiptStatus.Stored]: ["grNumber", "expectedDate", "trackingNumbers", "supplierName"],
} as const

export const GOODS_RECEIPT_STATUS_TO_LABEL: Record<GoodsReceiptStatus, string> = {
  [GoodsReceiptStatus.Incoming]: "Incoming",
  [GoodsReceiptStatus.SoftReceived]: "Soft Received",
  [GoodsReceiptStatus.PreCheckedIn]: "Soft Received",
  [GoodsReceiptStatus.CheckedIn]: "Checked In",
  [GoodsReceiptStatus.Stored]: "Stored",
} as const

// TODO (AK-7323): Refactor purchase orders and goods receipts to share a single document type to chip lookup.
export const GOODS_RECEIPT_DOCUMENT_TYPE_TO_CHIP_MAP: Partial<
  Record<FieldGroupEnum, { label: string; color: string }>
> = {
  ...Object.fromEntries(
    PURCHASE_ORDER_DOCUMENT_TYPE_OPTIONS.map(({ value, label }) => [
      value,
      { label, color: colors.tertiary.yellow["300"] },
    ])
  ),
}

export const GOODS_RECEIPT_STATUS_TO_DESKTOP_ROUTE = new Map<
  GoodsReceiptStatus,
  Extract<keyof FileRoutesByFullPath, string>
>([
  [GoodsReceiptStatus.Incoming, "/app/warehouse-receiving/incoming"],
  [GoodsReceiptStatus.SoftReceived, "/app/warehouse-receiving/soft-received"],
  [GoodsReceiptStatus.PreCheckedIn, "/app/warehouse-receiving/soft-received"],
  [GoodsReceiptStatus.CheckedIn, "/app/warehouse-receiving/checked-in"],
  [GoodsReceiptStatus.Stored, "/app/warehouse-receiving/stored"],
])

export const GOODS_RECEIPT_STATUS_TO_TABLET_ROUTE = new Map<
  GoodsReceiptStatus,
  Extract<keyof FileRoutesByFullPath, string>
>([
  [GoodsReceiptStatus.Incoming, "/app/tablet/warehouse-receiving/incoming"],
  [GoodsReceiptStatus.SoftReceived, "/app/tablet/warehouse-receiving/soft-received"],
  [GoodsReceiptStatus.PreCheckedIn, "/app/tablet/warehouse-receiving/soft-received"],
  [GoodsReceiptStatus.CheckedIn, "/app/tablet/warehouse-receiving/checked-in"],
  [GoodsReceiptStatus.Stored, "/app/tablet/warehouse-receiving/stored"],
])

export const GoodsReceiptPalletTestStatusToIconMap = new Map<ProductTestStatus, ReactElement>([
  [
    ProductTestStatus.Approved,
    <CheckCircleFilledOutlined key='approved' className='size-6 text-base text-tertiary-green-500' />,
  ],
  [ProductTestStatus.OnHold, <WarningOutlined key='on-hold' className='size-6 text-base text-tertiary-yellow-500' />],
  [ProductTestStatus.Rejected, <CancelFilledOutlined key='rejected' className='size-6 text-base text-secondary' />],
  [
    ProductTestStatus.DirectShip,
    <DoNotDisturbOnFilledOutlined key='direct-ship' className='size-6 text-base text-gray-400' />,
  ],
  [ProductTestStatus.Pending, <PendingOutlined key='pending' className='size-6 text-base text-gray-600' />],
])

export const GoodsReceiptPalletTestStatusToLabelMap = new Map<ProductTestStatus, string>([
  [ProductTestStatus.Approved, "Approved"],
  [ProductTestStatus.OnHold, "On Hold"],
  [ProductTestStatus.Rejected, "Rejected"],
  [ProductTestStatus.DirectShip, "Not Evaluated / Direct Ship"],
  [ProductTestStatus.Pending, "Pending"],
])

export const MARK_AS_NOT_RECEIVED_LABEL_MAP: Partial<Record<GoodsReceiptStatus, string>> = {
  [GoodsReceiptStatus.SoftReceived]: "Mark as Not Received",
  [GoodsReceiptStatus.Stored]: "Mark as Not Stored",
} as const

export const GOODS_RECEIPT_PALLET_ROW_LOT_EMPTY_FORM_VALUES: SoftReceivedLotFormValues = {
  goodsReceiptPalletRowLotId: null,
  lotNumber: "",
  batchNumber: "",
  packageUnitQuantity: null,
  packagingType: null,
  unitsCount: null,
  quantity: null,
}

export const GOODS_RECEIPT_STATUS_TO_CHILD_ROUTE: Partial<Record<GoodsReceiptStatus, string>> = {
  [GoodsReceiptStatus.PreCheckedIn]: "/finalize-check-in",
  [GoodsReceiptStatus.CheckedIn]: "/built-pallets",
} as const

export const GOODS_RECEIPT_PALLET_AS400_SYNC_PENDING_STATUSES: As400SyncStatus[] = [
  As400SyncStatus.Creating,
  As400SyncStatus.ReceivingDeleting,
  As400SyncStatus.WorkOrderDeleting,
]

export const GOODS_RECEIPT_POLL_INTERVAL = 3000

export const GOODS_RECEIPT_PALLETS_POLL_INTERVAL = 10000
